import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const FieldLabel = ({ children }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 6,
    }}
  >
    {children}
  </div>
);

const FocusInput = ({ value, onChange, placeholder, ...rest }) => (
  <input
    {...rest}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={inputStyle}
    onFocus={(e) => {
      e.target.style.borderColor = "#ff5100";
      e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
    }}
    onBlur={(e) => {
      e.target.style.borderColor = "#e5e7eb";
      e.target.style.boxShadow = "none";
    }}
  />
);

const CreateProduct = ({
  domain,
  show,
  user,
  client,
  handleClose,
  handleFormSubmit,
}) => {
  const [itemCode, setItemCode] = useState("");
  const [name, setName] = useState("");
  const [metadataFields, setMetadataFields] = useState([]);

  const resetForm = () => {
    setItemCode("");
    setName("");
    setMetadataFields([]);
  };

  useEffect(() => {
    if (!show) resetForm();
  }, [show]);

  const addMetadataField = () => {
    setMetadataFields([...metadataFields, { key: "", value: "" }]);
  };

  const removeMetadataField = (index) => {
    const updated = [...metadataFields];
    updated.splice(index, 1);
    setMetadataFields(updated);
  };

  const updateMetadataField = (index, keyOrValue, newValue) => {
    const updated = [...metadataFields];
    updated[index][keyOrValue] = newValue;
    setMetadataFields(updated);
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    const updated = [...metadataFields];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setMetadataFields(updated);
  };

  const moveFieldDown = (index) => {
    if (index === metadataFields.length - 1) return;
    const updated = [...metadataFields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setMetadataFields(updated);
  };

  const handleSubmit = async () => {
    if (!itemCode || !name) {
      alert("Please fill in both Item Code and Name.");
      return;
    }

    const metadata = { name };
    for (const field of metadataFields) {
      if (field.key && field.value) {
        metadata[field.key] = field.value;
      }
    }

    const payload = {
      code: itemCode,
      metadata,
      images: [],
      user,
      client,
    };

    try {
      const response = await fetch("https://api.iriscrypt.com/item", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        handleClose();
        handleFormSubmit();
        resetForm();
      } else {
        alert(response.status === 409 ? "An item with this code already exists!" : result?.detail || "Failed to add item.");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      alert("An error occurred.");
    }
  };

  return (
    <>
      <style>{`
        .create-product-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .create-product-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .create-product-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .create-product-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .meta-row:not(:last-child) {
          margin-bottom: 8px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="create-product-modal"
        centered
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-box"
                style={{ color: "#ff5100", fontSize: 14 }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>
                Create Product SKU
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                Add a new product to your inventory
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div>
            <FieldLabel>SKU Code *</FieldLabel>
            <FocusInput
              type="text"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
              placeholder="e.g. PRD-001"
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <FieldLabel>Product Name *</FieldLabel>
            <FocusInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hand Sanitizer"
            />
          </div>

          {/* Metadata section note */}
          <div
            style={{
              marginTop: 20,
              background: "#f0f9ff",
              border: "1.5px solid #bae6fd",
              borderRadius: 10,
              padding: "10px 14px",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <i
              className="fas fa-circle-info"
              style={{
                color: "#0284c7",
                fontSize: 13,
                marginTop: 1,
                flexShrink: 0,
              }}
            />
            <div style={{ fontSize: 12.5, color: "#0369a1", lineHeight: 1.6 }}>
              <span style={{ fontWeight: 700 }}>SKU Metadata</span> is for
              fixed product specs that apply to every unit of this SKU, e.g.
              volume, weight, size, or flavour. For batch-specific details like
              manufacturing date or expiry, use Variable Metadata when creating
              a tag assignment for your BleST tags.
            </div>
          </div>

          {metadataFields.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <FieldLabel>Metadata</FieldLabel>
              {metadataFields.map((field, index) => (
                <div
                  key={index}
                  className="meta-row"
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <FocusInput
                    type="text"
                    value={field.key}
                    onChange={(e) =>
                      updateMetadataField(index, "key", e.target.value)
                    }
                    placeholder="Key"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <FocusInput
                    type="text"
                    value={field.value}
                    onChange={(e) =>
                      updateMetadataField(index, "value", e.target.value)
                    }
                    placeholder="Value"
                    style={{ ...inputStyle, flex: 1 }}
                  />

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      flexShrink: 0,
                    }}
                  >
                    <button
                      onClick={() => moveFieldUp(index)}
                      disabled={index === 0}
                      title="Move up"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        border: "1.5px solid #e5e7eb",
                        background: "#fff",
                        color: index === 0 ? "#d1d5db" : "#6b7280",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                      }}
                    >
                      <i className="fas fa-chevron-up" />
                    </button>
                    <button
                      onClick={() => moveFieldDown(index)}
                      disabled={index === metadataFields.length - 1}
                      title="Move down"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        border: "1.5px solid #e5e7eb",
                        background: "#fff",
                        color:
                          index === metadataFields.length - 1
                            ? "#d1d5db"
                            : "#6b7280",
                        cursor:
                          index === metadataFields.length - 1
                            ? "not-allowed"
                            : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                      }}
                    >
                      <i className="fas fa-chevron-down" />
                    </button>
                  </div>

                  <button
                    onClick={() => removeMetadataField(index)}
                    title="Remove field"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      border: "1.5px solid #fecaca",
                      background: "#fff5f5",
                      color: "#dc2626",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={addMetadataField}
            style={{
              marginTop: metadataFields.length > 0 ? 10 : 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 14px",
              borderRadius: 9,
              border: "1.5px dashed #d1d5db",
              background: "transparent",
              color: "#6b7280",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#ff5100";
              e.currentTarget.style.color = "#ff5100";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <i
              className="fas fa-plus"
              style={{ fontSize: 11 }}
            />
            Add SKU metadata field
          </button>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleSubmit}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
            }}
          >
            Create Product SKU
            <i
              className="fas fa-arrow-right"
              style={{ fontSize: 13 }}
            />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateProduct;
